/**
 * The small controls the score setup panel is built from.
 *
 * They are here rather than inline so the panel itself reads as a list of what a score has
 * -- a title, a metre, its parts -- instead of as forty lines of Tailwind. None of them
 * knows anything about a score: each takes a value and hands back the next one.
 *
 * Text and numbers commit on blur and on Enter rather than on every keystroke, because a
 * keystroke that reached the document would make "Sonata" six undo steps and re-engrave the
 * page six times. The local value is re-synced whenever the one above it changes, so an undo
 * of a rename shows up in the box.
 */

import { useEffect, useState } from "react";
import type { ReactNode } from "react";

const CONTROL =
  "h-10 rounded-[10px] border border-rule bg-leaf px-2.5 text-[13px] text-ink outline-none focus:border-ink";

/** A label above its control, sized by the caller through `className`. */
export function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className={`flex flex-col gap-1 ${className ?? ""}`}>
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-quiet">
        {label}
      </span>
      {children}
    </label>
  );
}

/** A line of text, committed when the box is left or Enter is pressed. */
export function Text({
  label,
  value,
  placeholder,
  className,
  onCommit,
}: {
  label: string;
  value: string;
  placeholder?: string;
  className?: string;
  onCommit(next: string): void;
}): JSX.Element {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = (): void => {
    if (draft !== value) onCommit(draft);
  };

  return (
    <Field label={label} className={className}>
      <input
        value={draft}
        placeholder={placeholder}
        spellCheck={false}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
          if (event.key === "Escape") {
            event.preventDefault();
            setDraft(value);
          }
        }}
        className={`${CONTROL} w-full`}
      />
    </Field>
  );
}

/** A whole number within bounds, committed like {@link Text}. */
export function Count({
  label,
  value,
  min,
  max,
  className,
  onCommit,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  className?: string;
  onCommit(next: number): void;
}): JSX.Element {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  const commit = (): void => {
    const parsed = Number.parseInt(draft, 10);
    if (!Number.isFinite(parsed) || parsed < min || parsed > max) {
      setDraft(String(value));
      return;
    }
    if (parsed !== value) onCommit(parsed);
  };

  return (
    <Field label={label} className={className}>
      <input
        value={draft}
        inputMode="numeric"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
          if (event.key === "Escape") {
            event.preventDefault();
            setDraft(String(value));
          }
        }}
        className={`${CONTROL} w-full`}
      />
    </Field>
  );
}

/** One option of a {@link Choice}. */
export interface Option<T extends string> {
  readonly value: T;
  readonly label: string;
}

/**
 * A pick from a short list. An instrument is picked through `InstrumentPicker` instead, which
 * can be typed into.
 */
export function Choice<T extends string>({
  label,
  value,
  options,
  className,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly Option<T>[];
  className?: string;
  onChange(next: T): void;
}): JSX.Element {
  return (
    <Field label={label} className={className}>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
        className={`${CONTROL} w-full`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

/** A square button for the things a row does: add, remove, move. */
export function Tiny({
  title,
  disabled,
  onClick,
  children,
}: {
  title: string;
  disabled?: boolean;
  onClick(): void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className="h-10 w-10 shrink-0 rounded-[10px] border border-rule text-ink-faded hover:border-ink-quiet hover:text-ink disabled:opacity-35"
    >
      {children}
    </button>
  );
}
